// Selector searchable de cuentas contables externas (Alegra/Siigo).
// Usa react-select con paginacion virtual cliente-side: muestra 25 por vez
// y un boton "Cargar mas" que incrementa pageSize en +25 hasta agotar el
// dataset que el backend ya trajo.
//
// Cuando un proveedor real soporte paginacion server-side (ej: Siigo),
// el contrato del hook puede extenderse a useInfiniteQuery sin tocar
// este componente: solo cambiar de `chunks` a `fetchNextPage`.

import React, { useMemo, useState } from 'react';
import Select, { components as RSComponents } from 'react-select';
import { Button, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaCloudDownloadAlt, FaSyncAlt, FaPlus } from 'react-icons/fa';
import { useCuentasContablesExternas } from '../../hooks/useCuentasContablesExternas';

const CHUNK_SIZE = 25;

// react-select styles: ajustar al fontSize global del modal y dar zIndex
// alto al menu para que no quede tapado por el modal de bootstrap.
const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: 38 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: '6px 10px',
    backgroundColor: state.isFocused ? '#e7f1ff' : state.isSelected ? '#cfe2ff' : 'white',
    color: '#212529',
    cursor: 'pointer'
  })
};

// Render custom para cada opcion en el dropdown: muestra codigo + nombre +
// badges de clase y naturaleza. Bootstrap Badge no se ve bien dentro del
// menu de react-select (heredan padding raro), asi que uso spans.
function OptionRender({ data, ...props }) {
  return (
    <RSComponents.Option {...props} data={data}>
      <div className="d-flex justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 flex-grow-1 text-truncate">
          <code style={{ fontSize: 12, color: '#0d6efd' }}>{data.value}</code>
          <span className="text-truncate">{data.label}</span>
        </div>
        <div className="d-flex gap-1 flex-shrink-0">
          {data.raw.clase && (
            <span className="badge bg-info text-dark" style={{ fontSize: 10 }}>{data.raw.clase}</span>
          )}
          {data.raw.naturaleza && (
            <span
              className={`badge ${data.raw.naturaleza === 'Débito' ? 'bg-primary' : 'bg-warning text-dark'}`}
              style={{ fontSize: 10 }}
            >
              {data.raw.naturaleza}
            </span>
          )}
        </div>
      </div>
    </RSComponents.Option>
  );
}

// Render del valor seleccionado dentro del control (compacto)
function SingleValueRender({ data, ...props }) {
  return (
    <RSComponents.SingleValue {...props}>
      <code style={{ fontSize: 12, color: '#0d6efd' }} className="me-2">{data.value}</code>
      <span>{data.label}</span>
    </RSComponents.SingleValue>
  );
}

const SelectorCuentaExterna = ({ onSelect }) => {
  const { data, isLoading, isFetching, isError, error, refetch } = useCuentasContablesExternas();
  const [pageSize, setPageSize] = useState(CHUNK_SIZE);

  const cuentas = data?.cuentas || [];
  const configurado = data?.configurado;
  const providerNombre = data?.providerNombre || 'el proveedor';

  // Opciones visibles segun pageSize (paginacion virtual)
  const opcionesVisibles = useMemo(() => {
    return cuentas.slice(0, pageSize).map(c => ({
      value: c.codigo,
      label: c.nombre,
      raw: c
    }));
  }, [cuentas, pageSize]);

  const hayMas = pageSize < cuentas.length;
  const totalMostrado = Math.min(pageSize, cuentas.length);

  const handleSelect = (opt) => {
    if (!opt) return;
    if (onSelect) onSelect(opt.raw);
  };

  const handleCargarMas = () => {
    setPageSize(prev => prev + CHUNK_SIZE);
  };

  const handleRefrescar = () => {
    setPageSize(CHUNK_SIZE); // reset al refrescar
    refetch();
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted small py-2">
        <Spinner animation="border" size="sm" />
        Consultando cuentas del proveedor contable…
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="warning" className="py-2 small mb-0">
        No se pudo consultar el proveedor externo. {error?.message || ''}
        <Button size="sm" variant="link" onClick={() => refetch()} className="p-0 ms-2">
          Reintentar
        </Button>
      </Alert>
    );
  }

  if (!configurado) {
    return (
      <Alert variant="light" className="py-2 small mb-0 border">
        No hay proveedor contable activo o no esta configurado el endpoint de
        cuentas contables. Puedes crear la cuenta manualmente abajo.
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
        <FaCloudDownloadAlt className="text-primary" />
        <strong>Importar desde {providerNombre}</strong>
        <Badge bg="secondary">{cuentas.length} cuentas</Badge>
        <Button
          variant="link"
          size="sm"
          className="ms-auto p-0 text-decoration-none"
          onClick={handleRefrescar}
          disabled={isFetching}
          title="Refrescar cuentas del proveedor"
        >
          <FaSyncAlt /> Refrescar
        </Button>
      </div>

      <div className="d-flex gap-2 align-items-stretch">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <Select
            options={opcionesVisibles}
            onChange={handleSelect}
            placeholder={`Buscar cuenta por codigo o nombre... (${totalMostrado} de ${cuentas.length})`}
            isClearable
            isSearchable
            noOptionsMessage={() => 'Sin coincidencias en las cuentas cargadas'}
            styles={selectStyles}
            components={{ Option: OptionRender, SingleValue: SingleValueRender }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            value={null}
          />
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleCargarMas}
          disabled={!hayMas || isFetching}
          title={hayMas ? `Cargar siguientes ${CHUNK_SIZE} cuentas` : 'Ya no hay mas cuentas'}
          className="flex-shrink-0"
          style={{ whiteSpace: 'nowrap' }}
        >
          <FaPlus className="me-1" />
          {hayMas ? `Cargar +${CHUNK_SIZE}` : 'No hay mas'}
        </Button>
      </div>

      <small className="text-muted d-block mt-1">
        {hayMas
          ? `Mostrando ${totalMostrado} de ${cuentas.length}. Usa el buscador o "Cargar +${CHUNK_SIZE}" para ver mas.`
          : `Mostrando todas las ${cuentas.length} cuentas disponibles.`}
      </small>
    </div>
  );
};

export default SelectorCuentaExterna;
